import React, { useState } from 'react';
import { fmt, fmtPct, appliquerDecoupage } from '../utils/calcul';
import { Session, Decoupage } from '../types';
import './ResultatRepartition.css';

const TYPE_LABELS: Record<string, string> = { depot: 'Dépôt', retrait: 'Retrait', tous: 'Tous' };
const TYPE_CLASSES: Record<string, string> = {
  depot: 'tranche-col-depot',
  retrait: 'tranche-col-retrait',
  tous: 'tranche-col-tous',
};

function fmtH(h: number): string {
  return `${String(h).padStart(2, '0')}h`;
}
function fmtTime(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
}

interface Props {
  session: Session;
  decoupages: Decoupage[];
}

export default function ResultatRepartition({ session, decoupages }: Props) {
  const [vue, setVue] = useState<'tableau' | 'graphe' | 'decoupage'>('tableau');
  const [decoupageId, setDecoupageId] = useState('');
  const { montant, parts, base, resultats } = session;

  const LIMITE_PART = 5_000_000;
  const totalVerif = resultats.reduce((s, r) => s + r.part, 0);
  const totalPoids = parts.reduce((s, p) => s + p.poids, 0);
  const nbAjust = resultats.filter(r => r.ajuste).length;
  const exact = totalVerif === montant;
  const maxPart = Math.max(...resultats.map(r => r.part));
  const nbViolations = resultats.filter(r => r.part >= LIMITE_PART).length;

  const decoupageActif = decoupages.find(d => String(d.id) === decoupageId) ?? null;
  const resultatDecoupe = decoupageActif ? appliquerDecoupage(resultats, decoupageActif, base) : null;

  return (
    <div className="resultat-card">
      {/* ── En-tête ── */}
      <div className="res-header">
        <div className="res-header-left">
          <div className="res-badge">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Calcul effectué
          </div>
          <h2 className="res-title">{fmt(montant)} FCFA <span>en {resultats.length} parts</span></h2>
          <p className="res-sub">Arrondi au multiple de {base} · {session.date}</p>
        </div>
      </div>

      {/* ── Cartes résumé ── */}
      <div className="res-cards">
        <div className="res-card">
          <span className="res-card-label">Montant total</span>
          <span className="res-card-value">{fmt(montant)} <small>FCFA</small></span>
        </div>
        <div className="res-card">
          <span className="res-card-label">Nombre de parts</span>
          <span className="res-card-value">{resultats.length}</span>
        </div>
        <div className="res-card">
          <span className="res-card-label">Arrondi</span>
          <span className="res-card-value">× {base}</span>
        </div>
        <div className={`res-card ${exact ? 'res-card-ok' : 'res-card-err'}`}>
          <span className="res-card-label">Total vérifié</span>
          <span className="res-card-value">{fmt(totalVerif)} <small>FCFA</small>{exact ? ' ✔' : ' ✗'}</span>
        </div>
      </div>

      {/* ── Onglets ── */}
      <div className="res-tabs">
        <button className={`res-tab ${vue === 'tableau' ? 'active' : ''}`} onClick={() => setVue('tableau')}>Tableau détaillé</button>
        <button className={`res-tab ${vue === 'graphe' ? 'active' : ''}`} onClick={() => setVue('graphe')}>Visualisation</button>
        <button className={`res-tab ${vue === 'decoupage' ? 'active' : ''}`} onClick={() => setVue('decoupage')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          Découpage horaire
          {decoupages.length > 0 && <span className="res-tab-count">{decoupages.length}</span>}
        </button>
      </div>

      {/* ── Vue tableau ── */}
      {vue === 'tableau' && (
        <div className="res-table-wrap">
          <table className="res-table">
            <thead>
              <tr>
                <th>#</th><th>Bénéficiaire</th>
                <th>Plage horaire</th>
                <th className="align-right">Poids</th>
                <th className="align-right">Poids %</th>
                <th className="align-right">Part brute</th>
                <th className="align-right">Montant arrondi</th>
                <th className="align-right">Part %</th>
              </tr>
            </thead>
            <tbody>
              {resultats.map((r, i) => {
                const depasse = r.part >= LIMITE_PART;
                return (
                  <tr key={i} className={`${r.ajuste ? 'row-ajuste' : ''} ${depasse ? 'row-limite' : ''}`}>
                    <td className="cell-num">{i + 1}</td>
                    <td className="cell-nom">
                      {r.nom || `Personne ${i + 1}`}
                      {r.ajuste && <span className="badge-ajuste">★ ajusté</span>}
                      {depasse && <span className="badge-limite">⚠ limite</span>}
                    </td>
                    <td>
                      <span className="badge-plage">
                        {fmtTime(r.heureDebut, r.minuteDebut)}–{fmtTime(r.heureFin, r.minuteFin)}
                      </span>
                    </td>
                    <td className="align-right cell-mono">{r.poids}</td>
                    <td className="align-right cell-mono">{totalPoids > 0 ? ((r.poids / totalPoids) * 100).toFixed(1) + ' %' : '—'}</td>
                    <td className="align-right cell-mono cell-muted">{fmt(Math.round(r.brut))}</td>
                    <td className="align-right cell-mono cell-bold">{fmt(r.part)}</td>
                    <td className="align-right cell-mono">{fmtPct(r.pourcentage)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="row-total">
                <td></td><td><strong>TOTAL</strong></td>
                <td></td>
                <td className="align-right cell-mono"><strong>{totalPoids}</strong></td>
                <td className="align-right cell-mono"><strong>100,00 %</strong></td>
                <td className="align-right cell-mono">{fmt(montant)}</td>
                <td className="align-right cell-mono cell-bold">{fmt(totalVerif)}</td>
                <td className="align-right cell-mono"><strong>100,00 %</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── Vue graphe ── */}
      {vue === 'graphe' && (
        <div className="res-graphe">
          {resultats.map((r, i) => (
            <div key={i} className={`graphe-row ${r.ajuste ? 'graphe-row-ajuste' : ''}`}>
              <div className="graphe-label">
                <span className="graphe-name">{r.nom || `Personne ${i + 1}`}</span>
                {r.ajuste && <span className="badge-ajuste mini">★</span>}
                <span className="badge-plage" style={{ fontSize: '10px', padding: '1px 5px' }}>{fmtTime(r.heureDebut, r.minuteDebut)}–{fmtTime(r.heureFin, r.minuteFin)}</span>
              </div>
              <div className="graphe-bar-wrap">
                <div className="graphe-bar" style={{ width: `${(r.part / maxPart) * 100}%` }} />
              </div>
              <div className="graphe-values">
                <span className="graphe-montant">{fmt(r.part)} FCFA</span>
                <span className="graphe-pct">{fmtPct(r.pourcentage)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Vue découpage horaire ── */}
      {vue === 'decoupage' && (
        <div className="dec-vue">
          {decoupages.length === 0 ? (
            <div className="dec-vue-empty">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <p>Aucun découpage défini.</p>
              <p className="dec-vue-empty-sub">
                Créez des découpages dans l'onglet <strong>Découpages horaires</strong> pour ventiler
                le montant de chaque bénéficiaire sur des tranches de temps.
              </p>
            </div>
          ) : (
            <>
              <div className="dec-selector">
                <label className="dec-selector-label">Appliquer le découpage</label>
                <select value={decoupageId} onChange={e => setDecoupageId(e.target.value)} className="dec-selector-select">
                  <option value="">— Choisir un découpage —</option>
                  {decoupages.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                </select>
              </div>

              {decoupageActif && resultatDecoupe && (
                <div className="dec-matrix-wrap">
                  <table className="dec-matrix">
                    <thead>
                      <tr>
                        <th className="dec-col-ben">Bénéficiaire</th>
                        {decoupageActif.tranches.map((t, i) => (
                          <th key={i} className="align-right">
                            <span className="dec-th-heure">{fmtH(t.heureDebut)}–{fmtH(t.heureFin)}</span>
                            <span className={`dec-th-type ${TYPE_CLASSES[t.typeTransaction]}`}>{TYPE_LABELS[t.typeTransaction]}</span>
                            <span className="dec-th-pct">{t.pourcentage}%</span>
                          </th>
                        ))}
                        <th className="align-right dec-col-total">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultatDecoupe.map((r, i) => (
                        <tr key={i}>
                          <td className="cell-nom dec-cell-ben">{r.nom || `Personne ${i + 1}`}</td>
                          {r.tranchesDecoupage.map((t, j) => (
                            <td key={j} className="align-right cell-mono">{fmt(t.montant)}</td>
                          ))}
                          <td className="align-right cell-mono cell-bold dec-cell-total">{fmt(r.part)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="row-total">
                        <td><strong>TOTAL</strong></td>
                        {decoupageActif.tranches.map((_, j) => (
                          <td key={j} className="align-right cell-mono">
                            <strong>{fmt(resultatDecoupe.reduce((s, r) => s + r.tranchesDecoupage[j].montant, 0))}</strong>
                          </td>
                        ))}
                        <td className="align-right cell-mono cell-bold dec-cell-total"><strong>{fmt(totalVerif)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                  <p className="dec-matrix-note">Montants en FCFA · arrondi au multiple de {base} · total garanti exact par bénéficiaire</p>
                </div>
              )}

              {!decoupageActif && (
                <div className="dec-vue-hint">Sélectionnez un découpage ci-dessus pour voir la ventilation horaire de chaque bénéficiaire.</div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Alerte limite par part ── */}
      {vue !== 'decoupage' && nbViolations > 0 && (
        <div className="res-note res-note-limite">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span><strong>⚠ {nbViolations} part(s) dépassent 5 000 000 FCFA</strong> — la limite par bénéficiaire est de 5 000 000 FCFA. Augmentez le nombre de parts ou réduisez le montant.</span>
        </div>
      )}

      {/* ── Notes ── */}
      {vue !== 'decoupage' && nbAjust > 0 && (
        <div className="res-note res-note-warning">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span><strong>★ {nbAjust} part(s) ajustée(s)</strong> de ±{base} FCFA pour compenser l'écart d'arrondi.</span>
        </div>
      )}
      {vue !== 'decoupage' && nbAjust === 0 && (
        <div className="res-note res-note-ok">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>Aucun ajustement nécessaire : la somme des arrondis est exactement égale au montant.</span>
        </div>
      )}
    </div>
  );
}
